import type { FDBEntry } from '../types';

export interface FDBLiveQuery {
  mac?: string;
  vlan_id?: number;
  port?: string | number;
  type?: string;
  search?: string;
  problem?: 'all' | 'duplicates' | 'any';
  sort_by?: string;
  sort_desc?: boolean;
}

const compareText = (a: string, b: string): number => a < b ? -1 : a > b ? 1 : 0;

export function resolveFDBVlanFilter(selected: number | null, advancedInput: string, advancedEnabled: boolean): number | undefined {
  const selectedVlan = typeof selected === 'number' ? selected : undefined;
  const input = advancedEnabled ? advancedInput.trim() : '';
  if (!input) return selectedVlan;
  const advancedVlan = Number(input);
  if (!Number.isInteger(advancedVlan) || advancedVlan < 1 || advancedVlan > 4094) return -1;
  return selectedVlan !== undefined && selectedVlan !== advancedVlan ? -1 : advancedVlan;
}

// Transport fallback receives the same bounded Direct-FDB snapshot as Connect.
// Mirror the server's duplicate membership -> ordinary filters -> sort -> page order.
export function applyFDBLiveQuery(entries: FDBEntry[], query: FDBLiveQuery): { items: FDBEntry[]; duplicateCount: number } {
  const mac = query.mac?.trim().toLowerCase() ?? '';
  const portRaw = query.port == null ? '' : String(query.port).trim().toLowerCase();
  const portPrefix = portRaw.endsWith('*') && portRaw.length > 1;
  const port = portPrefix ? portRaw.slice(0, -1) : portRaw;
  const type = query.type?.trim().toLowerCase() ?? '';
  const search = query.search?.trim().toLowerCase() ?? '';
  const counts = new Map<string, number>();
  entries.forEach((row) => counts.set(row.mac_address.toLowerCase(), (counts.get(row.mac_address.toLowerCase()) ?? 0) + 1));
  const rows = entries.filter((entry) => {
    const entryMac = entry.mac_address.toLowerCase();
    const entryPort = entry.port_name.toLowerCase();
    return (query.problem !== 'duplicates' && query.problem !== 'any' || (counts.get(entryMac) ?? 0) > 1)
      && (!mac || entryMac.includes(mac))
      && (!query.vlan_id || entry.vlan_id === query.vlan_id)
      && (!port || entryPort === port || String(entry.port) === port || (portPrefix && entryPort.startsWith(port)))
      && (!type || type === 'all' || entry.type.toLowerCase() === type)
      && (!search || entryMac.includes(search) || entryPort.includes(search)
        || String(entry.vlan_id).includes(search) || String(entry.port).includes(search));
  });
  const duplicateCount = rows.filter((row) => (counts.get(row.mac_address.toLowerCase()) ?? 0) > 1).length;
  const sortBy = query.sort_by ?? 'mac_address';
  rows.sort((a, b) => {
    let primary = 0;
    if (sortBy === 'vlan_id') primary = a.vlan_id - b.vlan_id;
    else if (sortBy === 'port') primary = compareText(a.port_name.toLowerCase(), b.port_name.toLowerCase());
    else primary = compareText(a.mac_address.toLowerCase(), b.mac_address.toLowerCase());
    if (primary) return query.sort_desc ? -primary : primary;
    return a.vlan_id - b.vlan_id || compareText(a.port_name, b.port_name)
      || compareText(a.mac_address, b.mac_address) || a.port - b.port;
  });
  return { items: rows, duplicateCount };
}
